import { SingleDataIntermittence, WellboreDataIntermittenceDTO } from 'types/dataIntermittenceDto';
import { WellboreOutlierDTO } from 'types/outlierDto';

import {
  computeGlobalMappingIndex,
  MnemonicsRatioData,
} from '../ClosedRow/utils/mnemonicsRatioData';

export const getOutlierIndex = (numberFailure: number) => {
  if (numberFailure === 0) return 1;
  if (numberFailure === 1) return 0.8;
  if (numberFailure < 4) return 0.6;
  if (numberFailure < 6) return 0.4;

  return 0;
};

export const computeGlobalOutlierIndex = (outlier?: WellboreOutlierDTO) => {
  if (!outlier || Object.keys(outlier).length === 0) return 0;

  const outliersRateFailureNumber = Object.values(outlier)
    .map(singleOutlier => singleOutlier.outliersRate)
    .reduce((sum, val) => {
      if (val > 0.01) return sum + 1;

      return sum;
    }, 0);

  const outliersCountFailureNumber = Object.values(outlier)
    .map(singleOutlier => singleOutlier.outliers)
    .reduce((sum, val) => {
      if (val > 500) return sum + 1;

      return sum;
    }, 0);

  return (
    (getOutlierIndex(outliersRateFailureNumber) + getOutlierIndex(outliersCountFailureNumber)) / 2
  );
};

const computeSingleDataHoleIndex = (value: number, min: number, max: number) => {
  return value < min
    ? 1
    : value > max
    ? 0
    : 1 - (value > max ? max - min : value - min) / (max - min);
};

export const computeGlobalDataHoleIndex = (dataIntermittence?: WellboreDataIntermittenceDTO) => {
  if (!dataIntermittence || Object.keys(dataIntermittence).length === 0) return 0;
  const dataHoleIndexReducer = (
    accumulator: SingleDataIntermittence,
    currentValue: SingleDataIntermittence,
  ) => {
    const p50 = currentValue.p50 < 1800 ? currentValue.p50 : 1800;
    const p90 = currentValue.p90 < 1800 ? currentValue.p90 : 1800;

    return {
      p50: accumulator.p50 + p50 / Object.keys(dataIntermittence).length,
      p90: accumulator.p90 + p90 / Object.keys(dataIntermittence).length,
      max: Math.max(accumulator.max, currentValue.max),
    };
  };
  const averagedDataIntermittence = Object.values(dataIntermittence).reduce(dataHoleIndexReducer, {
    p50: 0,
    p90: 0,
    max: 0,
  });
  const p50Index = computeSingleDataHoleIndex(averagedDataIntermittence.p50, 30, 180);
  const p90Index = computeSingleDataHoleIndex(averagedDataIntermittence.p90, 60, 300);
  const maxIndex = computeSingleDataHoleIndex(averagedDataIntermittence.max, 60, 3600);

  return Math.round((100 * (p50Index + p90Index + maxIndex)) / 3) / 100;
};

export const computeDataQualityIndex = (
  mnemonicsRatioData: MnemonicsRatioData,
  dataConsistencyIndex: number,
  outlier?: WellboreOutlierDTO,
  dataIntermittence?: WellboreDataIntermittenceDTO,
) => {
  const globalMappingIndex = computeGlobalMappingIndex(mnemonicsRatioData);
  const globalOutlierIndex = computeGlobalOutlierIndex(outlier);
  const globalDataHoleIndex = computeGlobalDataHoleIndex(dataIntermittence);

  return (
    Math.round(
      (100 *
        (globalMappingIndex +
          globalOutlierIndex * 0.5 +
          globalDataHoleIndex +
          dataConsistencyIndex)) /
        3.5,
    ) / 100
  );
};
